// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import { Address, type AddressWireFormat, NewAddress } from '../Address/Address';
import {
  type DispatchCustomer,
  type DispatchCustomerWireFormat,
  NewDispatchCustomer,
} from '../DispatchCustomer/DispatchCustomer';
import Enums from '../Enums';
import {
  type IntegrationSummary,
  type IntegrationSummaryWireFormat,
  NewIntegrationSummary,
} from '../IntegrationSummary/IntegrationSummary';
import {
  type MarketSummary,
  type MarketSummaryWireFormat,
  NewMarketSummary,
} from '../MarketSummary/MarketSummary';
import { NewTier, type Tier, type TierWireFormat } from '../Tier/Tier';
import { NewUser, type User, type UserWireFormat } from '../User/User';

export class Company {
  readonly id: string;
  readonly name: string;
  readonly alternateID: string | null;
  readonly address: Address;
  readonly category: Enums.CompanyCategory;
  readonly notes: string | null;
  readonly dispatchCustomers: DispatchCustomer[];
  readonly marketSummaries: MarketSummary[];
  readonly isArchived: boolean;
  readonly user: User | null;
  readonly externalID: string | null;
  readonly integrationSummary: IntegrationSummary | null;
  readonly tiers: Tier[];

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.alternateID = data.alternateID === undefined ? null : data.alternateID;
    this.address = NewAddress(data.address);
    this.category = data.category === undefined ? Enums.CompanyCategory.Contractor : data.category;
    this.notes = data.notes === undefined ? null : data.notes;
    this.dispatchCustomers = (data.dispatchCustomers ?? []).map((dispatchCustomers: any) =>
      NewDispatchCustomer(dispatchCustomers),
    );
    this.marketSummaries = (data.marketSummaries ?? []).map((marketSummaries: any) =>
      NewMarketSummary(marketSummaries),
    );
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
    this.user =
      // eslint-disable-next-line no-nested-ternary
      (data.user ?? null) === null ? null : NewUser(data.user);
    this.externalID =
      // eslint-disable-next-line no-nested-ternary
      (data.externalID ?? null) === null
        ? null
        : data.externalID === undefined
          ? ''
          : data.externalID;
    this.integrationSummary =
      // eslint-disable-next-line no-nested-ternary
      (data.integration ?? data.integrationSummary ?? null) === null
        ? null
        : NewIntegrationSummary(data.integration ?? data.integrationSummary);
    this.tiers = (data.tiers ?? []).map((tiers: any) => NewTier(tiers));
  }

  static zero(): Company {
    const zeroValues: ExcludeMethodsDeep<Company> = {
      id: NIL_UUID,
      name: '',
      alternateID: null,
      address: Address.zero(),
      category: Enums.CompanyCategory.Contractor,
      notes: null,
      dispatchCustomers: [],
      marketSummaries: [],
      isArchived: false,
      user: null,
      externalID: null,
      integrationSummary: null,
      tiers: [],
    };
    return new Company(zeroValues);
  }

  toJSON(): { [key: string]: any } {
    return {
      id: this.id,
      name: this.name,
      alternateID: this.alternateID,
      address: this.address,
      category: this.category,
      notes: this.notes,
      dispatchCustomers: this.dispatchCustomers,
      marketSummaries: this.marketSummaries,
      isArchived: this.isArchived,
      user: this.user,
      externalID: this.externalID,
      integration: this.integrationSummary,
      tiers: this.tiers,
    };
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /**
   * lookupSublabels returns the proper sublabels for company lookups
   * based on the presence of the dispatch customer feature flag.
   */
  lookupSublabels(usesDispatchCustomer: boolean): string[] {
    const additionalID = this.getAdditionalIDSublabel();
    if (!usesDispatchCustomer) {
      return [additionalID];
    }
    const connectivitySublabel = this.dispatchCustomers.length > 0 ? 'Connected' : 'Disconnected';
    const creditDescriptionSublabel = this.getCreditDescriptionSublabel();
    return [connectivitySublabel, creditDescriptionSublabel, additionalID];
  }

  getAdditionalIDSublabel(): string {
    if (this.alternateID !== null) {
      return this.alternateID;
    }

    const codes = this.dispatchCustomers
      .map((dc) => dc.code)
      .filter((code) => code !== null && code !== '');
    return codes.join(' - ');
  }

  getCreditDescriptionSublabel(): string {
    const descriptions = this.dispatchCustomers
      .map((dc) => dc.creditStatusDescription)
      .filter((desc) => desc !== null && desc !== '');
    if (descriptions.length === 0) {
      return '';
    }
    return `Credit Status: ${descriptions.join(' - ')}`;
  }

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type CompanyWireFormat = {
  id: string;
  name: string;
  alternateID: string | null;
  address: AddressWireFormat;
  category: string;
  notes: string | null;
  dispatchCustomers: DispatchCustomerWireFormat[];
  marketSummaries: MarketSummaryWireFormat[];
  isArchived: boolean;
  user: UserWireFormat | null;
  externalID: string | null;
  integrationSummary: IntegrationSummaryWireFormat | null;
  tiers: TierWireFormat[];
};

type NewCompanyProps = PartialDeep<Company, { recurseIntoArrays: true }>;
export const NewCompany: (props: NewCompanyProps) => Company = (props) => new Company(props);

type NewCompanyFromWireFormatProps = PartialDeep<CompanyWireFormat, { recurseIntoArrays: true }>;
export const NewCompanyFromWireFormat: (props: NewCompanyFromWireFormatProps) => Company = (
  props,
) => new Company(props);
